import React, { useState } from 'react';
import { Play } from 'lucide-react';

/** @param {Record<string, string | number | boolean>} opts */
export function buildYoutubeNocookieSrc(videoId, opts = {}) {
  const params = new URLSearchParams({
    rel: '0',
    modestbranding: '1',
    playsinline: '1',
  });
  if (opts.controls === false) params.set('controls', '0');
  if (opts.mute) params.set('mute', '1');
  if (opts.autoplay) params.set('autoplay', '1');
  if (opts.loop && videoId) {
    params.set('loop', '1');
    params.set('playlist', videoId);
  }
  return `https://www.youtube-nocookie.com/embed/${videoId}?${params}`;
}

export function youtubePosterSrc(videoId) {
  if (!videoId) return '';
  return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
}

export function youtubeWatchUrl(videoId) {
  if (!videoId) return '';
  return `https://www.youtube.com/watch?v=${encodeURIComponent(videoId)}`;
}

/**
 * Inline YouTube player: **poster first**, then iframe after the visitor taps Play (counts as a user * gesture — helps autoplay and reduces “sign in” walls vs loading the iframe on page load).
 * Optional corner link opens youtube.com if the embed is blocked.
 */
export function YouTubeEmbed({
  videoId,
  title = 'SprintFit product video',
  className = '',
  aspectClassName = 'aspect-video',
  tone = 'dark',
  controls = true,
  loop = false,
  loading = 'lazy',
}) {
  const [activated, setActivated] = useState(false);
  if (!videoId) return null;

  const iframeSrc = buildYoutubeNocookieSrc(videoId, {
    autoplay: activated,
    mute: false,
    controls,
    loop,
  });

  const shell =
    tone === 'light'
      ? 'rounded-2xl border border-stone-200/90 bg-stone-900 shadow-[0_32px_64px_-28px_rgba(28,27,25,0.35)]'
      : tone === 'cinema'
        ? 'rounded-xl border border-white/[0.12] bg-black shadow-[0_40px_100px_-36px_rgba(0,0,0,0.95)] ring-1 ring-teal-400/15 ring-inset ring-white/5'
        : 'rounded-xl border border-white/[0.08] bg-black shadow-[0_24px_80px_-32px_rgba(0,0,0,0.85)]';

  return (
    <div className={`relative w-full overflow-hidden ${shell} ${aspectClassName} ${className}`}>
      {!activated ? (
        <>
          <img
            src={youtubePosterSrc(videoId)}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            loading={loading}
          />
          <div className="absolute inset-0 bg-black/40" aria-hidden />
          <button
            type="button"
            onClick={() => setActivated(true)}
            className="absolute inset-0 flex items-center justify-center text-white transition-colors hover:bg-black/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400/80 focus-visible:ring-inset"
            aria-label={`Play video: ${title}`}
          >
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/95 text-black shadow-lg ring-1 ring-black/10">
              <Play className="h-7 w-7 ml-1" fill="currentColor" aria-hidden />
            </span>
            <span className="sr-only">{title}</span>
          </button>
        </>
      ) : (
        <>
          <iframe
            className="absolute inset-0 h-full w-full"
            src={iframeSrc}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading={loading}
            referrerPolicy="strict-origin-when-cross-origin"
          />
          <a
            href={youtubeWatchUrl(videoId)}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute bottom-2 right-2 z-10 rounded bg-black/70 px-2 py-1 font-mono text-[9px] uppercase tracking-wider text-zinc-400 hover:text-white"
          >
            Open in YouTube
          </a>
        </>
      )}
    </div>
  );
}
